// D:\DhammaKnowledgeBase\chat-ui\src\auth\authService.js
import { auth, db } from '../config/firebaseConfig';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, runTransaction } from 'firebase/firestore';

const DEFAULT_SILA_QUERIES = 5; // Default queries for Sīla tier

// Helper function to ensure a user document exists or create a default one
// ADDED 'export' here
export const getOrCreateUserDocument = async (user) => {
  const userDocRef = doc(db, 'users', user.uid);
  const userDocSnap = await getDoc(userDocRef);

  if (!userDocSnap.exists()) {
    console.log("Creating new user document for:", user.uid);
    // Initialize new user with default Sīla tier
    await setDoc(userDocRef, {
      email: user.email,
      queriesLeft: DEFAULT_SILA_QUERIES,
      tier: 'Sīla',
      lastQueryReset: serverTimestamp(), // Record initial reset time
      createdAt: serverTimestamp(),
    });
    // Return the default data immediately for the context to use
    return {
      email: user.email,
      queriesLeft: DEFAULT_SILA_QUERIES,
      tier: 'Sīla',
      lastQueryReset: null, // As it's server timestamp, it's not available immediately client-side
      createdAt: null,
    };
  }
  return userDocSnap.data(); // Return existing user data
};

export const registerUser = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    // Ensure user document is created and initialized immediately after registration
    await getOrCreateUserDocument(user);
    return user;
  } catch (error) {
    console.error("Error registering user:", error);
    throw error;
  }
};

export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    // Ensure user document exists for existing users too (especially those registered before this logic)
    await getOrCreateUserDocument(user);
    return user;
  } catch (error) {
    console.error("Error logging in user:", error);
    throw error;
  }
};

export const logoutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error logging out:", error);
    throw error;
  }
};

// Function to update query count and handle daily reset
// Function to decrement query count
export const updateUserQueryCount = async (userId) => {
  const userDocRef = doc(db, 'users', userId);

  try {
    await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userDocRef);

      if (!userDoc.exists()) {
        throw new Error("User document does not exist!");
      }

      const userData = userDoc.data();
      const { queriesLeft } = userData;

      if (queriesLeft > 0) {
        transaction.update(userDocRef, { queriesLeft: queriesLeft - 1 });
      } else {
        throw new Error("You have reached your daily query limit.");
      }
    });
  } catch (error) {
    console.error("Error updating query count: ", error);
    throw error;
  }
};

// fetchUserData is less critical now that AuthContext uses onSnapshot,
// but can be kept if other parts of your app need a one-off fetch.
export const fetchUserData = async (userId) => {
  const userDocRef = doc(db, 'users', userId);
  const docSnap = await getDoc(userDocRef);
  if (docSnap.exists()) {
    return docSnap.data();
  } else {
    return null;
  }
};